import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  styled,
} from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Edit, Delete } from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken, base_url } from "../../config/config";

// Define the list of employees
// const employees = [
//   "Michael Otte",
//   "Daniel Herzog",
//   "Ronald Hasche",
//   "Rico Rasche",
//   "Jens Schubert",
//   "Jörg Melms",
//   "Maik Heinig",
//   "Marino Uhlemann",
//   "Jörg Maschner",
//   "Daniel Reddel",
//   "Axel Rentzsch",
//   "Franko Heinig",
//   "Matthias Kotte",
// ];

// Styled component for responsive typography
const ResponsiveTypography = styled(Typography)(({ theme }) => ({
  whiteSpace: "normal",
  wordWrap: "break-word",
}));

const Team = () => {
  // State declarations
  const [apiData, setApiData] = useState([]);
  const [refreshId, setRefreshId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [filterANR, setFilterANR] = useState("");
  const [overtimeFilter, setOvertimeFilter] = useState("");
  const [editData, setEditData] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newData, setNewData] = useState({
    user: "",
    ANR: "",
    description: "",
    date: "",
    StartTime: "",
    EndTime: "",
    Arbeiten: "",
    Menge: "",
    isOverTime: false,
  });
  const [employees, setEmployees] = useState([]);

  // State for selected date for daily report
  const [selectedDate, setSelectedDate] = useState("");

  // State for user feedback via Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error' | 'warning' | 'info'
  });
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        } else {
          console.error("Unexpected employee data format:", response.data);
          setSnackbar({
            open: true,
            message: response?.data?.message || "Fehler beim Abrufen der Daten",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setSnackbar({
          open: true,
          message: error.message || "Fehler beim Abrufen der Daten",
          severity: "error",
        });
      }
    };

    fetchEmployees();
  }, []);

  // State to manage submission status
  const [isSubmitting, setIsSubmitting] = useState(false);

  // *** New: Hourly rate state variable (you can change this value using the TextField below) ***
  const [hourlyRate, setHourlyRate] = useState(75);

  // Total work time in minutes (8 hours 15 minutes)
  const TOTAL_WORK_TIME_MINUTES = 8 * 60 + 15;

  // Function to close the Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Dropdown component for selecting employees
  const EmployeeSelect = ({ value, onChange }) => (
    <FormControl fullWidth>
      <InputLabel>Mitarbeiter</InputLabel>
      <Select value={value} onChange={onChange} label="Mitarbeiter">
        {employees.map((employee) => (
          <MenuItem key={employee.name} value={employee.name}>
            {employee.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Fetch API data
  useEffect(() => {
    const fetchData = async () => {
      const token = getToken();
      try {
        const response = await axios.get(`${base_url}/v1/getAllUserProjects`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status === 1) {
          setApiData(response.data.data);
          setFilteredData(response.data.data); // Also update filteredData
        } else {
          console.error("Error fetching API data:", response.data);
          setSnackbar({
            open: true,
            message: `Fehler beim Abrufen der Daten: ${response.data.message}`,
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching API data:", error);
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message || "Fehler beim Abrufen der Daten",
          severity: "error",
        });
      }
    };

    fetchData();
  }, [refreshId]);

  // Helper function to calculate total time and percentage
  const calculateTotalTime = (startTime, endTime) => {
    if (!startTime || !endTime) return { totalTime: "Pending", percentage: 0 };

    const parseTime = (timeStr) => {
      const [hours = "0", minutes = "0", seconds = "0"] = timeStr.split(":");
      return new Date(
        1970,
        0,
        1,
        Number(hours),
        Number(minutes),
        Number(seconds)
      );
    };

    const startDate = parseTime(startTime);
    let endDate = parseTime(endTime);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()))
      return { totalTime: "Pending", percentage: 0 };

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const diffMs = endDate - startDate;
    if (isNaN(diffMs) || diffMs <= 0)
      return { totalTime: "Pending", percentage: 0 };

    const breakfastStart = parseTime("08:45");
    const breakfastEnd = parseTime("09:00");
    const lunchStart = parseTime("11:45");
    const lunchEnd = parseTime("12:15");

    const adjustForBreak = (start, end, breakStart, breakEnd) => {
      if (end <= breakStart || start >= breakEnd) {
        return 0;
      }

      const overlapStart = new Date(Math.max(start, breakStart));
      const overlapEnd = new Date(Math.min(end, breakEnd));

      return overlapEnd - overlapStart;
    };

    let breakTimeMs = 0;
    breakTimeMs += adjustForBreak(
      startDate,
      endDate,
      breakfastStart,
      breakfastEnd
    );
    breakTimeMs += adjustForBreak(startDate, endDate, lunchStart, lunchEnd);

    const effectiveDiffMs = diffMs - breakTimeMs;
    if (isNaN(effectiveDiffMs) || effectiveDiffMs <= 0)
      return { totalTime: "Pending", percentage: 0 };

    const diffMins = Math.floor(effectiveDiffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    const totalMinutesInDay = 8 * 60 + 15; // 8 hours and 15 minutes in total
    const percentage = (diffMins / totalMinutesInDay) * 100;

    return {
      totalTime: `${diffHrs}h ${remainingMins}m`,
      percentage: percentage.toFixed(2),
    };
  };

  // Utility functions for handling overlapping time ranges and adjusted percentages
  const mergeTimeRanges = (timeRanges) => {
    if (timeRanges.length === 0) return [];
    
    // Helper function to parse time strings to Date objects
    const parseTimeToDate = (timeStr) => {
      // Handle formats like "HH:mm:ss", "HH:mm", or ensure it's properly formatted
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseInt(parts[2], 10) || 0;
      
      return new Date(1970, 0, 1, hours, minutes, seconds);
    };
    
    // Sort time ranges by start time
    const sorted = timeRanges
      .map(range => {
        const start = parseTimeToDate(range.startTime);
        let end = parseTimeToDate(range.endTime);
        
        // Handle next day scenario
        if (end < start) {
          end.setDate(end.getDate() + 1);
        }
        
        return {
          ...range,
          start: start,
          end: end
        };
      })
      .filter(range => !isNaN(range.start.getTime()) && !isNaN(range.end.getTime()))
      .sort((a, b) => a.start - b.start);

    if (sorted.length === 0) return [];

    const merged = [];
    let current = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      
      // If current range overlaps with next range
      if (current.end >= next.start) {
        // Merge the ranges by extending the end time
        current.end = new Date(Math.max(current.end, next.end));
        current.tasks = [...current.tasks, ...next.tasks];
      } else {
        // No overlap, add current to merged list and start new range
        merged.push(current);
        current = { ...next };
      }
    }
    
    // Add the last range
    merged.push(current);
    
    return merged;
  };

  const calculateBreakOverlapForRange = (start, end) => {
    const parseTime = (timeStr) => {
      const [hours = "0", minutes = "0", seconds = "0"] = timeStr.split(":");
      return new Date(1970, 0, 1, Number(hours), Number(minutes), Number(seconds));
    };

    const breakfastStart = parseTime("08:45");
    const breakfastEnd = parseTime("09:00");
    const lunchStart = parseTime("11:45");
    const lunchEnd = parseTime("12:15");

    const adjustForBreak = (rangeStart, rangeEnd, breakStart, breakEnd) => {
      if (rangeEnd <= breakStart || rangeStart >= breakEnd) {
        return 0;
      }

      const overlapStart = new Date(Math.max(rangeStart, breakStart));
      const overlapEnd = new Date(Math.min(rangeEnd, breakEnd));

      return overlapEnd - overlapStart;
    };

    let breakTimeMs = 0;
    breakTimeMs += adjustForBreak(start, end, breakfastStart, breakfastEnd);
    breakTimeMs += adjustForBreak(start, end, lunchStart, lunchEnd);

    return breakTimeMs;
  };

  const calculateAdjustedPercentages = (userTasks, date) => {
    // Group tasks by user and date
    const userDayTasks = userTasks.filter(task => task.date === date);
    
    if (userDayTasks.length === 0) return userTasks;

    // Prepare time ranges for merging
    const timeRanges = userDayTasks
      .filter(task => task.StartTime && task.EndTime && task.StartTime !== "pending" && task.EndTime !== "pending")
      .map(task => ({
        startTime: task.StartTime,
        endTime: task.EndTime,
        tasks: [{ 
          id: task._id || task.id || `${task.user}-${task.ANR}-${task.StartTime}`,
          duration: task.TotalTimeMinutes || 0,
          task: task
        }]
      }));

    if (timeRanges.length === 0) {
      // No valid time ranges, return original tasks with 0 adjusted percentage
      return userTasks.map(task => ({
        ...task,
        AdjustedPercentage: "0.00%"
      }));
    }

    // Merge overlapping time ranges
    const mergedRanges = mergeTimeRanges(timeRanges);
    
    // Calculate total unique working time (minus breaks) for the user on this day
    let totalUniqueMinutes = 0;
    
    mergedRanges.forEach(range => {
      const rangeDurationMs = range.end - range.start;
      const breakOverlapMs = calculateBreakOverlapForRange(range.start, range.end);
      const effectiveRangeMs = Math.max(0, rangeDurationMs - breakOverlapMs);
      totalUniqueMinutes += Math.floor(effectiveRangeMs / 60000);
    });

    // Calculate task contributions and adjusted percentages
    const taskAdjustments = {};
    const TOTAL_WORK_TIME_MINUTES = 8 * 60 + 15; // 8 hours 15 minutes

    mergedRanges.forEach(range => {
      const rangeDurationMs = range.end - range.start;
      const breakOverlapMs = calculateBreakOverlapForRange(range.start, range.end);
      const effectiveRangeMs = Math.max(0, rangeDurationMs - breakOverlapMs);
      const effectiveRangeMinutes = Math.floor(effectiveRangeMs / 60000);
      
      if (effectiveRangeMinutes > 0) {
        // Calculate total task duration within this range
        const totalTaskDurationInRange = range.tasks.reduce((sum, taskInfo) => {
          return sum + (taskInfo.duration || 0);
        }, 0);

        // Distribute the effective range time among tasks proportionally
        range.tasks.forEach(taskInfo => {
          const taskId = taskInfo.id;
          const taskDuration = taskInfo.duration || 0;
          
          if (totalTaskDurationInRange > 0) {
            // Proportional contribution
            const taskProportion = taskDuration / totalTaskDurationInRange;
            const taskContributionMinutes = effectiveRangeMinutes * taskProportion;
            
            taskAdjustments[taskId] = (taskAdjustments[taskId] || 0) + taskContributionMinutes;
          }
        });
      }
    });

    // Apply adjusted percentages to tasks
    return userTasks.map(task => {
      const taskId = task._id || task.id || `${task.user}-${task.ANR}-${task.StartTime}`;
      const adjustedMinutes = taskAdjustments[taskId] || 0;
      const adjustedPercentage = totalUniqueMinutes > 0 
        ? (adjustedMinutes / TOTAL_WORK_TIME_MINUTES) * 100 
        : 0;
      
      return {
        ...task,
        AdjustedPercentage: `${adjustedPercentage.toFixed(2)}%`,
        TotalTimeMinutes: task.TotalTime && task.TotalTime.includes("h") 
          ? (() => {
              const [hours, minutes] = task.TotalTime.split("h ").map(time => parseInt(time, 10) || 0);
              return hours * 60 + minutes;
            })()
          : 0
      };
    });
  };

  // Filter data based on date range, user, ANR, and overtime
  useEffect(() => {
    const filtered = apiData
      .map((project) => {
        const { totalTime, percentage } = calculateTotalTime(
          project.StartTime,
          project.EndTime
        );

        // Calculate TotalTimeMinutes for later use in adjusted percentage calculation
        const totalTimeMinutes = totalTime && totalTime.includes("h") 
          ? (() => {
              const [hours, minutes] = totalTime.split("h ").map(time => parseInt(time, 10) || 0);
              return hours * 60 + minutes;
            })()
          : 0;

        return {
          ...project,
          TotalTime: totalTime,
          TotalTimeMinutes: totalTimeMinutes,
          Percentage: `${percentage}%`,
        };
      })
      .filter((project) => {
        const projectDate = new Date(project.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        const matchesDateRange =
          (!start || projectDate >= start) && (!end || projectDate <= end);
        const matchesUser =
          !filterUser ||
          (project.user &&
            project.user.toLowerCase().includes(filterUser.toLowerCase()));

        // Exact match for ANR
        const matchesANR =
          !filterANR || (project.ANR && project.ANR.toString() === filterANR);

        const matchesOvertime =
          !overtimeFilter ||
          (overtimeFilter === "Ja" ? project.isOverTime : !project.isOverTime);

        return matchesDateRange && matchesUser && matchesANR && matchesOvertime;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    // Calculate adjusted percentages for overlapping tasks
    const processedData = [];
    const userDateGroups = {};

    // Group by user and date
    filtered.forEach(project => {
      const key = `${project.user}-${project.date}`;
      if (!userDateGroups[key]) {
        userDateGroups[key] = [];
      }
      userDateGroups[key].push(project);
    });

    // Process each user-date group to calculate adjusted percentages
    Object.values(userDateGroups).forEach(userDayTasks => {
      if (userDayTasks.length > 0) {
        const adjustedTasks = calculateAdjustedPercentages(userDayTasks, userDayTasks[0].date);
        processedData.push(...adjustedTasks);
      }
    });

    // Sort the processed data
    const finalData = processedData.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredData(finalData);
  }, [startDate, endDate, apiData, filterUser, filterANR, overtimeFilter]);

  // Define table columns with responsiveness
  const columns = useMemo(
    () => [
      {
        accessorKey: "user",
        header: "Mitarbeiter",
        minSize: 150,
        maxSize: 250,
      },
      {
        accessorKey: "ANR",
        header: "ANR",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "Percentage",
        header: "Percentage",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "AdjustedPercentage",
        header: "Adjusted %",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "Menge",
        header: "Menge",
        minSize: 80,
        maxSize: 120,
      },
      {
        accessorKey: "description",
        header: "Beschreibung",
        minSize: 150,
        maxSize: 250,
      },
      {
        accessorKey: "date",
        header: "Datum",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "StartTime",
        header: "Startzeit",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "EndTime",
        header: "Endzeit",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "TotalTime",
        header: "Gesamtzeit",
        minSize: 100,
        maxSize: 150,
      },
      {
        accessorKey: "Arbeiten",
        header: "Arbeiten",
        minSize: 200,
        maxSize: 300,
      },
      {
        accessorKey: "isOverTime",
        header: "Überstunden",
        minSize: 100,
        maxSize: 150,
        Cell: ({ cell }) => (cell.getValue() ? "Ja" : "Nein"),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        minSize: 150,
        maxSize: 200,
        Cell: ({ row }) => (
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditOpen(row.original)}
              style={{ marginRight: "8px" }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Delete />}
              onClick={() => handleDelete(row.original._id)}
            >
              Delete
            </Button>
          </>
        ),
      },
    ],
    []
  );

  // Handle export to Excel
  const handleExport = () => {
    const visibleData = filteredData.map((row) => ({
      user: row.user,
      ANR: row.ANR,
      description: row.description,
      date: row.date,
      StartTime: row.StartTime,
      EndTime: row.EndTime,
      TotalTime: row.TotalTime,
      Percentage: row.Percentage,
      AdjustedPercentage: row.AdjustedPercentage,
      Arbeiten: row.Arbeiten,
      isOverTime: row.isOverTime ? "Ja" : "Nein",
    }));

    const worksheet = XLSX.utils.json_to_sheet(visibleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Projects");
    XLSX.writeFile(workbook, "filtered_projects.xlsx");
  };

  // Handle export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");
    doc.text("Gefilterte Projekte", 14, 16);

    const tableColumn = [
      "Mitarbeiter",
      "ANR",
      "Menge",
      "Beschreibung",
      "Datum",
      "Startzeit",
      "Endzeit",
      "Gesamtzeit",
      "Percentage",
      "Adjusted %",
      "Arbeiten",
      "Überstunden",
    ];

    const tableRows = filteredData.map((row) => [
      row.user,
      row.ANR,
      row.Menge,
      row.description,
      row.date,
      row.StartTime,
      row.EndTime,
      row.TotalTime,
      row.Percentage,
      row.AdjustedPercentage,
      row.Arbeiten,
      row.isOverTime ? "Ja" : "Nein",
    ]);

    // Calculate the total sum of all TotalTime in minutes
    let totalMinutes = 0;
    filteredData.forEach((row) => {
      if (row.TotalTime && row.TotalTime.includes("h")) {
        const [hours, minutes] = row.TotalTime.split("h ").map((time) =>
          parseInt(time, 10)
        );
        totalMinutes += hours * 60 + minutes;
      }
    });

    // Convert total minutes to hours and minutes format
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    const totalTimeFormatted = `${totalHours}h ${remainingMinutes}m`;

    // Calculate the total cost using the hourlyRate state variable
    const totalCost = (totalMinutes / 60) * hourlyRate;

    // Add the rows to the PDF
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 10, cellPadding: 3 },
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: {
        fillColor: (row, data) => {
          return row.cells[10].raw === "Ja" ? [231, 76, 60] : [255, 255, 255];
        },
      },
    });

    let finalY = doc.lastAutoTable.finalY + 10;

    // Check if there is enough space to add the summary rows, else add a new page
    if (finalY + 20 > doc.internal.pageSize.height) {
      doc.addPage();
      finalY = 20; // Reset Y position for new page
    }
    doc.text(`Gesamtzeit: ${totalTimeFormatted}`, 14, finalY);
    doc.text(`Gesamtkosten: €${totalCost.toFixed(2)}`, 14, finalY + 10);

    doc.save("filtered_projects.pdf");
  };

  // Handle custom export (Büttner Export)
  const handleCustomExport = () => {
    const formattedData = filteredData.map((row) => {
      const auftragNr = row.ANR ? row.ANR.split("-")[0] : ""; // Extract Auftrag-Nr (first part of ANR)

      let az = 0; // Initialize az
      if (
        row.TotalTime &&
        row.TotalTime.includes("h") &&
        row.TotalTime.includes("m")
      ) {
        // Safely split TotalTime if it exists in "Xh Ym" format
        const timeParts = row.TotalTime.split("h ");
        const hours = parseInt(timeParts[0]) || 0;
        const minutes = parseInt(timeParts[1]?.replace("m", "")) || 0;
        az = hours * 100 + (minutes * 100) / 60;
      }

      // Format az and laufzeit with commas instead of points
      const azFormatted = (az * 0.01).toFixed(2).replace(".", ",");
      const laufzeitFormatted = (az * 0.01).toFixed(2).replace(".", ",");

      // Return formatted row data
      return {
        datum: row.date || "",
        "Auftrag-Nr.": auftragNr || "",
        "anr-un": "",
        jahr: "",
        az: azFormatted,
        menge: row.Menge || "",
        artikel: row.description || "",
        name: row.user || "",
        Kunde: "",
        laufzeit: laufzeitFormatted,
        kosten: "",
        "Zulage 10%": "",
        "Zulage 25%": "",
        "Zulage 50%": "",
        Spalte1: row.ANR || "",
        Spalte2: "",
      };
    });

    // Create Excel worksheet from formatted data
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ExportedData");
    XLSX.writeFile(workbook, "custom_export.xlsx");
  };

  // Export daily report as PDF
  const exportDailyReport = () => {
    // Check if a date is selected
    if (!selectedDate) {
      setSnackbar({
        open: true,
        message: "Bitte wählen Sie ein Datum aus.",
        severity: "warning",
      });
      return;
    }

    // Filter data for the selected date
    const filteredDataForDate = filteredData.filter(
      (row) => row.date === selectedDate
    );

    if (filteredDataForDate.length === 0) {
      setSnackbar({
        open: true,
        message: "Keine Daten für das ausgewählte Datum gefunden.",
        severity: "info",
      });
      return;
    }

    // Export the filtered data to PDF
    handleExportSingleDayPDF(filteredDataForDate);
  };

  const handleExportSingleDayPDF = (filteredData) => {
    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    let currentY = 16;

    // Title
    doc.setFontSize(16);
    doc.text(`Tagesbericht für ${selectedDate}`, pageWidth / 2, currentY, {
      align: "center",
    });
    currentY += 10;

    const tableColumn = [
      "ANR",
      "Menge",
      "Beschreibung",
      "Startzeit",
      "Endzeit",
      "Gesamtzeit",
      "Prozent (%)",
      "Adjusted %",
      "Arbeiten",
      "Überstunden",
    ];

    // Group data by employee
    const employeeDataMap = {};
    filteredData.forEach((row) => {
      if (!employeeDataMap[row.user]) {
        employeeDataMap[row.user] = [];
      }
      employeeDataMap[row.user].push(row);
    });

    // Iterate over each employee and add their section
    Object.keys(employeeDataMap).forEach((employee, index) => {
      // Calculate total minutes worked
      let totalMinutes = 0;
      employeeDataMap[employee].forEach((row) => {
        if (row.TotalTime && row.TotalTime.includes("h")) {
          const [hours, minutes] = row.TotalTime.split("h ").map((time) =>
            parseInt(time, 10)
          );
          totalMinutes += hours * 60 + minutes;
        }
      });

      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      const totalTimeFormatted = `${totalHours}h ${remainingMinutes}m`;

      // Calculate total percentage of workday
      const totalPercentage =
        ((totalMinutes / TOTAL_WORK_TIME_MINUTES) * 100).toFixed(2) + "%";

      // Employee Heading
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185); // Optional: Change text color for headings
      doc.text(`Mitarbeiter: ${employee}`, margin, currentY);
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0); // Reset text color to black
      doc.text(
        `Gesamtarbeitszeit: ${totalTimeFormatted}`,
        margin,
        currentY + 6
      );
      doc.text(
        `Gesamtprozent des Arbeitstages: ${totalPercentage}`,
        margin,
        currentY + 12
      );
      currentY += 18;

      // Sort employee data by StartTime
      const sortedEmployeeData = employeeDataMap[employee].sort((a, b) => {
        const timeA = a.StartTime.split(":").map(Number);
        const timeB = b.StartTime.split(":").map(Number);
        return timeA[0] - timeB[0] || timeA[1] - timeB[1];
      });

      // Employee Table
      const tableRows = sortedEmployeeData.map((row) => [
        row.ANR,
        row.Menge,
        row.description,
        row.StartTime,
        row.EndTime,
        row.TotalTime,
        row.Percentage,
        row.Arbeiten,
        row.isOverTime ? "Ja" : "Nein",
      ]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: currentY,
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 3 },
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] }, // Optional: Header background color
        bodyStyles: {
          // Highlight rows with overtime
          fillColor: (row, data) => {
            return row.cells[8].raw === "Ja" ? [231, 76, 60] : [255, 255, 255];
          },
        },
        didDrawPage: (data) => {
          currentY = data.cursor.y + 10;
        },
        // Add spacing after table
        afterPageContent: () => {
          currentY += 5; // Additional space if a new page is added
        },
      });

      // Update currentY position after table
      currentY = doc.lastAutoTable.finalY + 10; // Increased from +5 to +10 for more space

      // Add multiple separator lines for better separation
      const numberOfLines = 2; // Number of separator lines
      const lineSpacing = 2; // Space between each separator line

      for (let i = 0; i < numberOfLines; i++) {
        if (currentY > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setDrawColor(41, 128, 185); // Matching the header color
        doc.setLineWidth(0.5);
        doc.line(margin, currentY, pageWidth - margin, currentY); // Draw a horizontal line
        currentY += lineSpacing; // Space after each line
      }

      currentY += 5; // Additional space after the separator lines

      // Check if adding a new employee exceeds the page height
      if (currentY > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
    });

    doc.save(`daily_report_${selectedDate}.pdf`);
  };

  // Handle opening of edit modal
  const handleEditOpen = (rowData) => {
    setEditData(rowData);
    setIsEditOpen(true);
  };

  // Handle closing of edit modal
  const handleEditClose = () => {
    setIsEditOpen(false);
  };

  // Handle saving of edited data
  const handleEditSave = async () => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${base_url}/v1/updateProjectbyId`,
        editData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (
        response.data &&
        (response.data.status === 1 || response.data.success)
      ) {
        setIsEditOpen(false);
        setSnackbar({
          open: true,
          message: "Projekt erfolgreich aktualisiert!",
          severity: "success",
        });

        // Force a refresh by updating refreshId
        setRefreshId(Date.now());
      } else {
        throw new Error(response.data?.message || "Update fehlgeschlagen");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message,
        severity: "error",
      });
    }
  };

  // Handle opening of add modal
  const handleAddOpen = () => {
    setIsAddOpen(true);
  };

  // Handle closing of add modal
  const handleAddClose = () => {
    setIsAddOpen(false);
  };

  // Handle saving of new data
  const handleAddSave = async () => {
    // Basic form validation
    if (
      !newData.user ||
      !newData.ANR ||
      !newData.description ||
      !newData.date ||
      !newData.StartTime ||
      !newData.EndTime ||
      !newData.Menge ||
      !newData.Arbeiten
    ) {
      setSnackbar({
        open: true,
        message: "Bitte füllen Sie alle erforderlichen Felder aus.",
        severity: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Get auth token for API request
      const token = getToken();

      const response = await axios.post(
        `${base_url}/v1/createUserProjectDashboard`,
        newData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 1) {
        setApiData([...apiData, response.data.data]);
        setIsAddOpen(false);
        setSnackbar({
          open: true,
          message: "Neues Projekt erfolgreich hinzugefügt!",
          severity: "success",
        });
        setRefreshId(Date.now()); // Trigger data refresh
        // Reset newData
        setNewData({
          user: "",
          ANR: "",
          description: "",
          date: "",
          StartTime: "",
          EndTime: "",
          Arbeiten: "",
          Menge: "",
          isOverTime: false,
        });
      } else {
        setSnackbar({
          open: true,
          message: `Fehler beim Hinzufügen: ${response.data.message}`,
          severity: "error",
        });
        console.error("Error adding project:", response.data.message);
      }
    } catch (error) {
      console.error("Error adding project:", error);

      // Show specific message for auth errors
      if (error.response && error.response.status === 401) {
        setSnackbar({
          open: true,
          message: "Nicht autorisiert. Bitte melden Sie sich erneut an.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Fehler beim Hinzufügen: ${error.message}`,
          severity: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deletion of a project
  const handleDelete = async (id) => {
    try {
      // Get auth token for API request
      const token = getToken();

      console.log("Deleting project with id:", id);
      const response = await axios.delete(
        `${base_url}/v1/deleteUserProject/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 1) {
        setApiData((prevData) =>
          prevData.filter((project) => project._id !== id)
        );
        setSnackbar({
          open: true,
          message: "Projekt erfolgreich gelöscht!",
          severity: "success",
        });
        setRefreshId(Date.now()); // Trigger data refresh
      } else {
        setSnackbar({
          open: true,
          message: `Fehler beim Löschen: ${response.data.message}`,
          severity: "error",
        });
        console.error("Error deleting project:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting project:", error);

      // Show specific message for auth errors
      if (error.response && error.response.status === 401) {
        setSnackbar({
          open: true,
          message: "Nicht autorisiert. Bitte melden Sie sich erneut an.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Fehler beim Löschen: ${error.message}`,
          severity: "error",
        });
      }
    }
  };

  return (
    <div style={{ margin: "20px" }}>
      {/* Filters Section */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {/* Date Range Filters */}
        <TextField
          label="Startdatum"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="Enddatum"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* User Filter */}
        <TextField
          label="Filter Mitarbeiter"
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        />

        {/* ANR Filter with Placeholder */}
        <TextField
          label="Filter ANR"
          value={filterANR}
          onChange={(e) => setFilterANR(e.target.value.trim())}
          placeholder="Geben Sie die genaue ANR ein"
        />

        {/* New: Hourly Rate Input */}
        <TextField
          label="Stundensatz (€)"
          type="number"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(Number(e.target.value))}
          InputProps={{ inputProps: { min: 0 } }}
        />

        {/* Export Buttons */}
        <Button variant="contained" color="primary" onClick={handleExport}>
          Exportieren Sie gefilterte Daten nach Excel
        </Button>

        <Button variant="contained" color="primary" onClick={handleExportPDF}>
          Exportieren Sie gefilterte Daten in PDF
        </Button>

        {/* Add New Data Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddOpen}
          style={{ marginBottom: "20px" }}
        >
          Neue Daten hinzufügen
        </Button>

        {/* Custom Export Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleCustomExport}
        >
          Büttner Export
        </Button>
      </div>

      {/* Daily Report Export Section */}
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="reportDate">Datum auswählen:</label>
        <input
          type="date"
          id="reportDate"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ marginLeft: "10px", marginRight: "10px" }}
        />
        <button onClick={exportDailyReport}>
          Exportieren Sie einen Tagesbericht
        </button>
      </div>

      {/* Overtime Filter */}
      <div style={{ marginBottom: "20px" }}>
        <Typography>Überstunden</Typography>
        <ToggleButtonGroup
          value={overtimeFilter}
          exclusive
          onChange={(e, newFilter) => setOvertimeFilter(newFilter)}
          aria-label="Überstunden Filter"
        >
          <ToggleButton value="Ja" aria-label="Ja">
            Ja
          </ToggleButton>
          <ToggleButton value="Nein" aria-label="Nein">
            Nein
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Data Table */}
      <div
        id="table-container"
        style={{
          overflowX: "auto", // Enable horizontal scrolling
          overflowY: "hidden", // Optional: Hide vertical overflow if not needed
          marginBottom: "20px",
        }}
      >
        {filteredData.length > 0 ? (
          <MaterialReactTable
            columns={columns}
            data={filteredData}
            enableFullScreenToggle={false}
            enableColumnResizing
            initialState={{ density: "comfortable" }}
            muiTableContainerProps={{
              sx: { maxHeight: "70vh" }, // Adjust as needed
            }}
            muiTableBodyCellProps={{
              sx: { whiteSpace: "normal", wordWrap: "break-word" }, // Allow text wrapping
            }}
            muiTableHeadCellProps={{
              sx: { whiteSpace: "normal", wordWrap: "break-word" }, // Allow header text wrapping
            }}
          />
        ) : (
          <ResponsiveTypography>
            Keine Projekte entsprechen den aktuellen Filtern.
          </ResponsiveTypography>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onClose={handleEditClose}>
        <DialogTitle>Projekt bearbeiten</DialogTitle>
        <DialogContent>
          {editData && (
            <form>
              <EmployeeSelect
                value={editData.user}
                onChange={(e) =>
                  setEditData({ ...editData, user: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="ANR"
                fullWidth
                value={editData.ANR}
                onChange={(e) =>
                  setEditData({ ...editData, ANR: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Beschreibung"
                fullWidth
                value={editData.description}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Datum"
                type="date"
                fullWidth
                value={editData.date}
                onChange={(e) =>
                  setEditData({ ...editData, date: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="dense"
                label="Menge"
                type="number"
                fullWidth
                value={editData.Menge}
                onChange={(e) =>
                  setEditData({ ...editData, Menge: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="dense"
                label="Startzeit"
                type="time"
                fullWidth
                value={editData.StartTime}
                onChange={(e) =>
                  setEditData({ ...editData, StartTime: e.target.value })
                }
              />

              {/* Swap Button */}
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  const temp = editData.StartTime;
                  setEditData({
                    ...editData,
                    StartTime: editData.EndTime,
                    EndTime: temp,
                  });
                }}
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                Zeiten tauschen <SwapVertIcon />
              </Button>

              <TextField
                margin="dense"
                label="Endzeit"
                type="time"
                fullWidth
                value={editData.EndTime}
                onChange={(e) =>
                  setEditData({ ...editData, EndTime: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Arbeiten"
                fullWidth
                value={editData.Arbeiten}
                onChange={(e) =>
                  setEditData({ ...editData, Arbeiten: e.target.value })
                }
              />
              <ToggleButtonGroup
                value={editData.isOverTime ? "Ja" : "Nein"}
                exclusive
                onChange={(e, newFilter) =>
                  setEditData({ ...editData, isOverTime: newFilter === "Ja" })
                }
                aria-label="Überstunden"
                fullWidth
              >
                <ToggleButton value="Ja" aria-label="Ja">
                  Ja
                </ToggleButton>
                <ToggleButton value="Nein" aria-label="Nein">
                  Nein
                </ToggleButton>
              </ToggleButtonGroup>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="secondary">
            Abbrechen
          </Button>
          <Button onClick={handleEditSave} color="primary">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={isAddOpen} onClose={handleAddClose}>
        <DialogTitle>Neues Projekt hinzufügen</DialogTitle>
        <DialogContent>
          <form>
            <EmployeeSelect
              value={newData.user}
              onChange={(e) => setNewData({ ...newData, user: e.target.value })}
            />
            <TextField
              margin="dense"
              label="ANR"
              fullWidth
              value={newData.ANR}
              onChange={(e) => setNewData({ ...newData, ANR: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Beschreibung"
              fullWidth
              value={newData.description}
              onChange={(e) =>
                setNewData({ ...newData, description: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Datum"
              type="date"
              fullWidth
              value={newData.date}
              onChange={(e) => setNewData({ ...newData, date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              margin="dense"
              label="Startzeit"
              type="time"
              fullWidth
              value={newData.StartTime}
              onChange={(e) =>
                setNewData({ ...newData, StartTime: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Endzeit"
              type="time"
              fullWidth
              value={newData.EndTime}
              onChange={(e) =>
                setNewData({ ...newData, EndTime: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Menge"
              type="number"
              fullWidth
              value={newData.Menge}
              onChange={(e) =>
                setNewData({ ...newData, Menge: e.target.value })
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              margin="dense"
              label="Arbeiten"
              fullWidth
              value={newData.Arbeiten}
              onChange={(e) =>
                setNewData({ ...newData, Arbeiten: e.target.value })
              }
            />
            <ToggleButtonGroup
              value={newData.isOverTime ? "Ja" : "Nein"}
              exclusive
              onChange={(e, newFilter) =>
                setNewData({ ...newData, isOverTime: newFilter === "Ja" })
              }
              aria-label="Überstunden"
              fullWidth
            >
              <ToggleButton value="Ja" aria-label="Ja">
                Ja
              </ToggleButton>
              <ToggleButton value="Nein" aria-label="Nein">
                Nein
              </ToggleButton>
            </ToggleButtonGroup>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose} color="secondary">
            Abbrechen
          </Button>
          <Button
            onClick={handleAddSave}
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Speichern..." : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Team;
